import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Tabs, { TabConfigType } from '@uikit/old/Tabs/Tabs';
import PageHeader from '@uikit/old/PageHeader/PageHeader';
import Button from '@uikit/Form/Button/Button';

import urls from '@constants/urls';
import { BillingPublisherPaymentMethod } from 'respona_api/generated/common_pb';
import { fetchPublisherBillingDetails } from '@redux/thunks/billings-publisher.thunks';
import { billingPublisherDetailsSelector } from '@redux/selectors/billings-publisher.selectors';
import { checkIsAnyNotLoaded } from '@utils/loadingChecks';
import { addNotification } from '@redux/actions/notifications.actions';

import BillingDetailsListCard from './_components/BillingDetailsListCard/BillingDetailsListCard';
import InvoicesTable from './_components/InvoicesTable/InvoicesTable';
import WalletCard from './_components/WalletCard/WalletCard';
import WithdrawForm from './_components/WithdrawForm/WithdrawForm';
import WithdrawalsTable from './_components/WithdrawalsTable/WithdrawalsTable';

import { isPayoutMethodConfirmed } from './utils';

import './SettingsBilling.scss';

function SettingsBilling(): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const { data, loadingStatus: publisherLs } = useSelector(billingPublisherDetailsSelector);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isWithdrawListOpen, setIsWithdrawListOpen] = useState(false);
  const [isHighlightBillingDetails, setIsHighlightBillingDetails] = useState(false);

  useEffect(() => {
    if (checkIsAnyNotLoaded(publisherLs)) {
      fetchPublisherBillingDetails(true)(dispatch);
    }
  }, [publisherLs]);

  const visiblePaymentSettings = useMemo(() => {
    return data?.paymentSettingsList;
  }, [data?.paymentSettingsList]);
  const withdrawableSettings = useMemo(
    () => visiblePaymentSettings.filter(isPayoutMethodConfirmed),
    [visiblePaymentSettings]
  );

  const availableFunds = Number(data?.availableFunds || 0);
  const pendingFunds = Number(data?.pendingFunds || 0);
  const inWithdrawalFunds = Number(data?.inWithdrawalFunds || 0);
  const hasVisibleMethods = visiblePaymentSettings.length > 0;

  const handleWithdrawSuccess = () => {
    setIsWithdrawOpen(false);
    setIsWithdrawListOpen(true);
  };

  const handleOpenWithdraw = () => {
    if (!hasVisibleMethods) {
      dispatch(addNotification({ type: 'warning', title: 'Please add your billing details.' }));
      return;
    }
    setIsWithdrawOpen(true);
  };

  const tabs: TabConfigType[] = [
    {
      key: 'invoices',
      title: 'Invoices',
      icon: 'invoice',
      iconType: 'stroke',
      renderContent: () => <InvoicesTable />,
    },
    {
      key: 'withdrawals',
      title: 'Withdrawals',
      icon: 'downloadInCircle',
      iconType: 'fill',
      renderContent: () => <WithdrawalsTable />,
    },
  ];

  return (
    <div className="settings-billing" data-testid="publisher-billing-page">
      <PageHeader
        title="Billing"
        renderHeaderActions={() => (
          <Button
            type="ghost"
            onClick={() => history.push(urls.SETTINGS_BILLING_WITHDRAWAL_FEES)}
            data-testid="publisher-billing-withdrawal-fees"
          >
            Withdrawal fees
          </Button>
        )}
      />
      <div className="settings-billing__content">
        <div className="settings-billing__cards">
          <WalletCard
            availableFunds={availableFunds}
            pendingFunds={pendingFunds}
            inWithdrawalFunds={inWithdrawalFunds}
            isWithdrawOpen={isWithdrawOpen}
            isWithdrawing={false}
            hasWithdrawableMethod={withdrawableSettings.length > 0}
            hasVisibleMethods={hasVisibleMethods}
            onOpenWithdraw={handleOpenWithdraw}
            onCloseWithdraw={() => setIsWithdrawOpen(false)}
          >
            <WithdrawForm
              paymentSettings={withdrawableSettings}
              availableFunds={availableFunds}
              onCancel={() => setIsWithdrawOpen(false)}
              onSuccess={handleWithdrawSuccess}
              onBillingAddressError={() => setIsHighlightBillingDetails(true)}
            />
          </WalletCard>

          <BillingDetailsListCard
            paymentSettings={visiblePaymentSettings}
            isHighlighted={isHighlightBillingDetails}
            onUpdate={() => history.push(urls.SETTINGS_BILLING_DETAILS)}
          />
        </div>

        <div className="settings-billing__tables">
          <Tabs
            tabs={tabs}
            activeTab={isWithdrawListOpen ? tabs[1].key : ''}
            testId="publisher-billing-tabs"
          />
        </div>
      </div>
    </div>
  );
}

export default SettingsBilling;
